/**
 * Table.tsx — top-level wonders-table renderer. Connects the WS for the
 * given match ulid, fetches cards, renders the board + hands + side
 * panel, and dispatches TableActions on user input.
 */

import { DndContext, type DragEndEvent, useDroppable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import { Coins, Target, Zap } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useBoardMode } from '../board-modes.js';
import { useCards } from '../hooks/useCards.js';
import { useMatchSocket } from '../hooks/useMatchSocket.js';
import { useTelemetry } from '../hooks/useTelemetry.js';
import { useWebRTC } from '../hooks/useWebRTC.js';
import type {
  Placement,
  PlayerId,
  PublicTableState,
  RealmName,
  TableAction,
  TableMove,
  ZoneRef,
} from '../types.js';
import { AIQuestionChip } from './AIQuestionChip.js';
import { ApprovalModal } from './ApprovalModal.js';
import { Board } from './Board.js';
import { BoardBroadcast } from './BoardBroadcast.js';
import { BoardCarousel } from './BoardCarousel.js';
import { BoardFullscreen2D, GameLogPanel, ResourcePill, ZoneStack } from './BoardFullscreen2D.js';
import { BoardModeSwitcher } from './BoardModeSwitcher.js';
import { BoardOverhead3D } from './BoardOverhead3D.js';
import { CounterMenu } from './CounterMenu.js';
import { type EmoteEvent, EmoteOverlay } from './EmoteOverlay.js';
import { EmotePalette } from './EmotePalette.js';
import { Hand } from './Hand.js';
import { MulliganModal } from './MulliganModal.js';
import { NotificationOverlay } from './NotificationOverlay.js';
import { RulesHelperPanel } from './RulesHelperPanel.js';
import { SidePanel } from './SidePanel.js';
import { VoiceChat } from './VoiceChat.js';
import { ZonePeek } from './ZonePeek.js';

type Props = {
  matchUlid: string;
  /** The viewer's PlayerId (from /api/me). */
  viewer: PlayerId;
};

export function Table({ matchUlid, viewer }: Props) {
  const cards = useCards();
  const telemetry = useTelemetry();
  const { mode: boardMode } = useBoardMode();
  const [state, setState] = useState<PublicTableState | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    cardId: string;
    placement?: Placement;
  } | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [emoteEvents, setEmoteEvents] = useState<readonly EmoteEvent[]>([]);
  const [peer, setPeer] = useState<string>('');
  /**
   * Click-to-play selection. Lifts the "what's selected and where it came
   * from" state up to Table so every renderer (3D, classic, fullscreen2d,
   * carousel) shares one interaction model.
   *   - Click a hand card → select with source 'hand'
   *   - Click your in-play card → select with source 'realm'
   *   - Click any realm → MoveCard from selectedSource to that realm
   *   - Click again on selected → deselect
   */
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<
    { kind: 'hand' } | { kind: 'realm'; realm: RealmName } | null
  >(null);
  /** Transient action toast for immediate user feedback. Auto-clears after 2s. */
  const [toast, setToast] = useState<string | null>(null);
  /** Event-tick counter — bumps on every WS event_delta so future
   *  observers (replacing the removed CoachPanel) can react. */
  const [eventTick, setEventTick] = useState(0);
  /** Accumulated TableActionApplied events. Drives the game-log panel
   *  in BoardFullscreen2D — every move (including resource adjusts and
   *  cost-paying moves) shows up so opponents can audit play. */
  const [allEvents, setAllEvents] = useState<readonly unknown[]>([]);
  /** Once we've seen state.firstPlayer at least once we set this to
   *  suppress the roll banner in subsequent renders. */
  const [firstPlayerAck, setFirstPlayerAck] = useState(false);
  /** Peek modal for the deck/abyss/banished/tokens stacks in Classic mode. */
  const [classicPeek, setClassicPeek] = useState<
    | { kind: 'deck'; cardIds: readonly string[] }
    | { kind: 'abyss'; cardIds: readonly string[] }
    | { kind: 'banished'; cardIds: readonly string[] }
    | { kind: 'tokens'; cardIds: readonly string[] }
    | null
  >(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  // Track recent emotes from event_delta payloads. We sniff the
  // TableActionApplied stream rather than maintain a separate channel.
  const captureEmote = useCallback((events: readonly unknown[]) => {
    const additions: EmoteEvent[] = [];
    for (const ev of events) {
      const e = ev as {
        kind?: string;
        payload?: {
          actor?: string;
          action?: { kind?: string; emoji?: string; x?: number; y?: number };
        };
        at?: number;
      };
      if (
        e?.kind === 'TableActionApplied' &&
        e?.payload?.action?.kind === 'EmoteReaction' &&
        typeof e.payload.action.emoji === 'string'
      ) {
        additions.push({
          id: `${e.payload.actor ?? 'anon'}-${e.at ?? Date.now()}`,
          emoji: e.payload.action.emoji,
          x: e.payload.action.x,
          y: e.payload.action.y,
          at: e.at ?? Date.now(),
        });
      }
    }
    if (additions.length > 0) {
      setEmoteEvents((prev) => [...prev, ...additions].slice(-20));
    }
  }, []);

  // ─── WS lifecycle ───
  const socket = useMatchSocket(matchUlid, {
    onWelcome: (msg) => {
      setState(msg.publicState);
      const opp = msg.publicState.players.find((p) => p !== viewer);
      if (opp) setPeer(opp);
      telemetry.emit({ kind: 'match_lifecycle', event: 'joined' }, matchUlid);
    },
    onEventDelta: (msg) => {
      setState(msg.publicState);
      captureEmote(msg.events);
      setAllEvents((prev) => [...prev, ...msg.events]);
      setEventTick((n) => n + 1);
    },
    onWebRTCSignal: (msg) => {
      // The hook is mounted unconditionally below; it's safe to call
      // receiveSignal even when no PC is set up yet (it no-ops).
      void voice.receiveSignal(msg.payload);
    },
    onMoveRejected: (msg) => {
      setMoveError(msg.reason);
      setTimeout(() => setMoveError(null), 4000);
    },
  });

  // Voice chat. Hook is always mounted; useEffect inside is gated on
  // `peer` being set, so it stays idle until the welcome arrives.
  const voice = useWebRTC({
    viewer,
    peer,
    sendSignal: (payload) => socket.sendWebRTCSignal(peer, payload),
  });

  // Auto-prune emote overlay state every few seconds.
  useEffect(() => {
    const t = setInterval(() => {
      const cutoff = Date.now() - 5000;
      setEmoteEvents((prev) => prev.filter((e) => e.at > cutoff));
    }, 2000);
    return () => clearInterval(t);
  }, []);

  // ─── Action dispatch ───
  const dispatchAction = useCallback(
    (action: TableAction) => {
      const move: TableMove = { kind: 'TableAction', action };
      socket.sendMove(move);
      telemetry.emit({ kind: 'feature_used', feature: actionFeatureName(action) }, matchUlid);
    },
    [socket, telemetry, matchUlid],
  );

  // ─── Drag-end → MoveCard dispatch ───
  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      if (!e.over) return;
      const data = e.active.data.current as { cardId: string; source: ZoneRef } | undefined;
      const dropTarget = e.over.data.current as
        | {
            kind: ZoneRef['kind'];
            realm?: RealmName;
            owner: PlayerId;
            position?: 'top' | 'bottom';
          }
        | undefined;
      if (!data || !dropTarget) return;
      // Free-form placement: when dropping into a realm, compute the
      // card's centre relative to the realm tile's bounding rect and
      // pass it as normalized x,y in [0.05, 0.95] (clamped so cards
      // never sit at the absolute edge). Falls back to centre if
      // dnd-kit doesn't surface translated rects (rare — happens in
      // synthetic tests).
      const computeDropXY = (): { x: number; y: number } => {
        const overRect = e.over?.rect;
        const activeRect = e.active.rect.current.translated;
        if (!overRect || !activeRect) return { x: 0.5, y: 0.5 };
        const cx = activeRect.left + activeRect.width / 2;
        const cy = activeRect.top + activeRect.height / 2;
        const x = (cx - overRect.left) / overRect.width;
        const y = (cy - overRect.top) / overRect.height;
        return {
          x: Math.min(0.95, Math.max(0.05, x)),
          y: Math.min(0.95, Math.max(0.05, y)),
        };
      };
      const to: ZoneRef =
        dropTarget.kind === 'realm' && dropTarget.realm
          ? {
              kind: 'realm',
              realm: dropTarget.realm,
              owner: dropTarget.owner,
              ...computeDropXY(),
            }
          : dropTarget.kind === 'realm'
            ? // shouldn't happen — realm targets always have a realm name
              data.source
            : dropTarget.kind === 'deck'
              ? {
                  kind: 'deck',
                  owner: dropTarget.owner,
                  ...(dropTarget.position && { position: dropTarget.position }),
                }
              : { kind: dropTarget.kind, owner: dropTarget.owner };

      dispatchAction({
        kind: 'MoveCard',
        cardId: data.cardId,
        from: data.source,
        to,
      });
    },
    [dispatchAction],
  );

  // ─── Context-menu handlers ───
  const onCardContextMenu = useCallback(
    (cardId: string, e: React.MouseEvent) => {
      e.preventDefault();
      const placement = findPlacement(state, cardId);
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        cardId,
        ...(placement !== null && { placement }),
      });
    },
    [state],
  );

  // ─── Loading / not-yet-welcomed states ───
  if (cards.status === 'error') {
    return (
      <Centered>
        <div className="text-rose-400">Failed to load cards: {cards.error}</div>
      </Centered>
    );
  }
  if (cards.status !== 'ready' || !state) {
    return (
      <Centered>
        <div className="text-cosmos-400">Loading match…</div>
      </Centered>
    );
  }

  const opponent = state.players.find((p) => p !== viewer) ?? state.players[1];
  const viewerZones = state.zones[viewer];
  const hand =
    viewerZones && 'hand' in viewerZones && Array.isArray(viewerZones.hand) ? viewerZones.hand : [];

  // AI Coach panel was replaced by the clarifying-question chip
  // (AIQuestionChip — bottom-right). The chip is rendered globally
  // below alongside the approval modal.
  void eventTick;
  const coachOverlay: React.ReactNode = null;
  const globalOverlays = (
    <>
      <ApprovalModal state={state} viewer={viewer} dispatchAction={dispatchAction} />
      <AIQuestionChip />
      <NotificationOverlay events={allEvents} viewer={viewer} cards={cards.cards} />
    </>
  );

  // Mulligan modal — fires for ranked matches at game start, before
  // the first action. The modal is one-shot per player (state.mulliganUsed
  // flips to true once the action lands). In sandbox mode the flag is
  // never set so the modal stays hidden.
  const myZones = state.zones[viewer];
  const myHand =
    myZones && 'hand' in myZones ? (myZones.hand as readonly string[]) : ([] as readonly string[]);
  const myDeckCards =
    myZones && 'deck' in myZones ? (myZones.deck as readonly string[]) : ([] as readonly string[]);
  const myAbyssCards =
    myZones && 'abyss' in myZones
      ? (myZones.abyss as readonly string[])
      : ([] as readonly string[]);
  const myBanishedCards =
    myZones && 'banished' in myZones
      ? (myZones.banished as readonly string[])
      : ([] as readonly string[]);
  const myTokenCards =
    myZones && 'tokens' in myZones
      ? (myZones.tokens as readonly string[])
      : ([] as readonly string[]);
  const myResources = state.resources?.[viewer] ?? { energy: 0, actions: 0, hoard: 0 };
  const round = Math.max(1, state.round ?? 0);
  // Mulligan window fires at match start in BOTH sandbox and ranked.
  // The modal is the only UI gating interaction (full-screen backdrop)
  // until the player picks up-to-3 cards to bury or clicks "Keep all".
  // One-shot per player — once mulliganUsed flips true the modal hides
  // and play proceeds normally.
  const showMulligan = state.mulliganUsed?.[viewer] === false;
  const mulliganOverlay = showMulligan ? (
    <MulliganModal
      hand={myHand}
      cards={cards.cards}
      imageUrl={cards.imageUrl}
      onSubmit={(cardIds) => {
        dispatchAction({ kind: 'Mulligan', cardIds });
      }}
    />
  ) : null;

  // Board-mode dispatch — non-classic modes are alternative layouts
  // sharing the same WS-driven state. They wrap their own DndContext.
  if (boardMode === 'fullscreen2d') {
    return (
      <>
        <DndContext onDragEnd={onDragEnd}>
          <BoardFullscreen2D
            state={state}
            viewer={viewer}
            opponent={opponent}
            cards={cards.cards}
            imageUrl={cards.imageUrl}
            onSeizeStone={(idx, player) => {
              const realm = state.realms[idx];
              if (!realm) return;
              dispatchAction({ kind: 'MoveStone', realm: realm.name, to: player });
            }}
            dispatchAction={dispatchAction}
            events={allEvents}
          />
        </DndContext>
        {coachOverlay}
        {mulliganOverlay}
        {globalOverlays}
      </>
    );
  }
  if (boardMode === 'carousel') {
    return (
      <>
        <BoardCarousel
          state={state}
          viewer={viewer}
          opponent={opponent}
          cards={cards.cards}
          imageUrl={cards.imageUrl}
          onSeizeStone={(idx, player) => {
            const realm = state.realms[idx];
            if (!realm) return;
            dispatchAction({ kind: 'MoveStone', realm: realm.name, to: player });
          }}
          dispatchAction={dispatchAction}
        />
        {coachOverlay}
        {mulliganOverlay}
        {globalOverlays}
      </>
    );
  }
  if (boardMode === 'overhead3d') {
    return (
      <>
        <BoardOverhead3D
          state={state}
          viewer={viewer}
          selectedCardId={selectedCardId}
          selectedSource={selectedSource}
          toast={toast}
          onSelectHandCard={(cardId) => {
            if (cardId === null) {
              setSelectedCardId(null);
              setSelectedSource(null);
              return;
            }
            setSelectedCardId(cardId);
            setSelectedSource({ kind: 'hand' });
          }}
          onClickInPlayCard={(cardId, realm, wasSelected) => {
            if (wasSelected) {
              setSelectedCardId(null);
              setSelectedSource(null);
              return;
            }
            setSelectedCardId(cardId);
            setSelectedSource({ kind: 'realm', realm });
          }}
          onPlayCardToRealm={(cardId, realm) => {
            const from =
              selectedSource?.kind === 'realm'
                ? ({ kind: 'realm', realm: selectedSource.realm, owner: viewer } as const)
                : ({ kind: 'hand', owner: viewer } as const);
            dispatchAction({
              kind: 'MoveCard',
              cardId,
              from,
              to: { kind: 'realm', realm, owner: viewer, x: 0.5, y: 0.5 },
            });
            setToast(`Played ${cardId} → ${realm}`);
            setSelectedCardId(null);
            setSelectedSource(null);
          }}
          onSeizeStoneAt={(idx, claim) => {
            const realm = state.realms[idx];
            if (!realm) return;
            dispatchAction({
              kind: 'MoveStone',
              realm: realm.name,
              to: claim ? viewer : null,
            });
            setToast(claim ? `Seized ${realm.name}` : `Released ${realm.name}`);
          }}
        />
        {coachOverlay}
        {mulliganOverlay}
        {globalOverlays}
      </>
    );
  }

  if (boardMode === 'broadcast') {
    return <BoardBroadcast state={state} viewer={viewer} cards={cards.cards} />;
  }

  return (
    <DndContext onDragEnd={onDragEnd}>
      <div className="relative flex h-screen w-screen overflow-hidden bg-cosmos-950">
        {/* Floating board-mode switcher — top-right of the viewport. The
            parent must be `relative` for the absolute pin to anchor here
            instead of the body (the previous root-level absolute pinned
            it to the body and ended up underneath the side-panel scroll
            container, swallowing clicks). */}
        <div className="absolute right-3 top-3 z-50">
          <BoardModeSwitcher />
        </div>
        <main className="flex flex-col flex-1 overflow-hidden">
          {/* Opponent hand strip */}
          <OpponentStrip opponent={opponent} state={state} />

          {/* Board */}
          <div className="flex-1 overflow-auto">
            <Board
              realms={state.realms}
              viewer={viewer}
              opponent={opponent}
              cards={cards.cards}
              imageUrl={cards.imageUrl}
              currentRound={round}
              onPlacementContextMenu={onCardContextMenu}
              onSeizeStone={(idx, player) => {
                const realm = state.realms[idx];
                if (!realm) return;
                dispatchAction({ kind: 'MoveStone', realm: realm.name, to: player });
              }}
            />
          </div>

          {/* Player chrome — three pile columns (Deck · Abyss · Exile),
              each a top stack (drop on top) plus a Bury slot under it
              (drop to bottom of pile). Resource pills and Draw / Pass /
              End-Round sit to the right. Mirrors Fullscreen2D parity. */}
          <div className="flex items-start gap-3 border-cosmos-800 border-t bg-cosmos-900/80 px-3 py-3">
            <PileColumn
              label="DECK"
              tone="amber"
              count={myDeckCards.length}
              onClick={() => setClassicPeek({ kind: 'deck', cardIds: myDeckCards })}
              topId="zone-deck-top"
              topData={{ kind: 'deck', owner: viewer, position: 'top' }}
              buryId="zone-deck-bottom"
              buryData={{ kind: 'deck', owner: viewer, position: 'bottom' }}
              topTooltip="Drag to draw the top card. Drop on top to put cards back. Click to peek."
              {...(myDeckCards[0]
                ? {
                    topCardDragId: `pile-drag-deck-${myDeckCards[0]}`,
                    topCardDragData: {
                      cardId: myDeckCards[0],
                      source: { kind: 'deck', owner: viewer },
                    },
                  }
                : {})}
            />
            <PileColumn
              label="ABYSS"
              tone="rose"
              count={myAbyssCards.length}
              onClick={() => setClassicPeek({ kind: 'abyss', cardIds: myAbyssCards })}
              topId="zone-abyss-top"
              topData={{ kind: 'abyss', owner: viewer, position: 'top' }}
              buryId="zone-abyss-bottom"
              buryData={{ kind: 'abyss', owner: viewer, position: 'bottom' }}
              topTooltip="Drag to retrieve the top of abyss. Drop to discard. Click to view."
              {...(myAbyssCards[0]
                ? {
                    topCardDragId: `pile-drag-abyss-${myAbyssCards[0]}`,
                    topCardDragData: {
                      cardId: myAbyssCards[0],
                      source: { kind: 'abyss', owner: viewer },
                    },
                  }
                : {})}
            />
            <PileColumn
              label="BANISHMENT"
              tone="violet"
              count={myBanishedCards.length}
              onClick={() => setClassicPeek({ kind: 'banished', cardIds: myBanishedCards })}
              topId="zone-banished-top"
              topData={{ kind: 'banished', owner: viewer, position: 'top' }}
              buryId="zone-banished-bottom"
              buryData={{ kind: 'banished', owner: viewer, position: 'bottom' }}
              topTooltip="Drag to retrieve the top of banishment. Drop to banish. Click to view."
              {...(myBanishedCards[0]
                ? {
                    topCardDragId: `pile-drag-banished-${myBanishedCards[0]}`,
                    topCardDragData: {
                      cardId: myBanishedCards[0],
                      source: { kind: 'banished', owner: viewer },
                    },
                  }
                : {})}
            />
            <PileColumn
              label="TOKENS"
              tone="amber"
              count={myTokenCards.length}
              onClick={() => setClassicPeek({ kind: 'tokens', cardIds: myTokenCards })}
              topId="zone-tokens-top"
              topData={{ kind: 'tokens', owner: viewer, position: 'top' }}
              buryId="zone-tokens-bottom"
              buryData={{ kind: 'tokens', owner: viewer, position: 'bottom' }}
              topTooltip="Token deck — drag the top token into play. Click to view all."
              {...(myTokenCards[0]
                ? {
                    topCardDragId: `pile-drag-tokens-${myTokenCards[0]}`,
                    topCardDragData: {
                      cardId: myTokenCards[0],
                      source: { kind: 'tokens', owner: viewer },
                    },
                  }
                : {})}
            />
            <ResourcePill
              icon={<Zap className="h-3.5 w-3.5 text-amber-300" />}
              value={myResources.energy}
              label="Energy"
              onAdjust={(delta) =>
                dispatchAction({ kind: 'AdjustResource', resource: 'energy', delta })
              }
            />
            <ResourcePill
              icon={<Target className="h-3.5 w-3.5 text-rose-300" />}
              value={myResources.actions}
              label="Actions"
              onAdjust={(delta) =>
                dispatchAction({ kind: 'AdjustResource', resource: 'actions', delta })
              }
            />
            <ResourcePill
              icon={<Coins className="h-3.5 w-3.5 text-violet-300" />}
              value={myResources.hoard}
              label="Hoard"
              onAdjust={(delta) =>
                dispatchAction({ kind: 'AdjustResource', resource: 'hoard', delta })
              }
            />
            <span className="font-mono text-[11px] text-cosmos-400">
              R{round}/7 {state.initiative === viewer ? '· your turn' : '· opp turn'}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const top = myDeckCards[0];
                  if (!top) return;
                  dispatchAction({
                    kind: 'MoveCard',
                    cardId: top,
                    from: { kind: 'deck', owner: viewer },
                    to: { kind: 'hand', owner: viewer },
                  });
                }}
                disabled={myDeckCards.length === 0}
                className="rounded bg-violet-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-600 disabled:opacity-40"
              >
                Draw
              </button>
              {(() => {
                const opp = state.players.find((p) => p !== viewer) ?? null;
                const pending = state.pendingUndo ?? null;
                const last = state.lastActor ?? null;
                const pendingByMe = pending?.requestedBy === viewer;
                const pendingByOpp = !!pending && !pendingByMe;
                const label = pendingByMe
                  ? 'Undo requested…'
                  : pendingByOpp
                    ? 'Confirm undo'
                    : 'Undo';
                const title = pendingByMe
                  ? 'Click to cancel your undo request'
                  : pendingByOpp
                    ? "Confirm opponent's undo: rolls back both sides' last actions"
                    : last === viewer
                      ? 'Roll back your last action and restore actions/energy'
                      : last === opp
                        ? 'Request undo — opponent must confirm by also clicking Undo'
                        : 'Nothing to undo';
                const disabled = !pending && !last;
                return (
                  <button
                    type="button"
                    onClick={() => dispatchAction({ kind: 'Undo' })}
                    disabled={disabled}
                    title={title}
                    className={
                      pendingByOpp
                        ? 'rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-40'
                        : pendingByMe
                          ? 'rounded border border-amber-700 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-900/30 disabled:opacity-40'
                          : 'rounded border border-cosmos-700 px-3 py-1.5 text-xs hover:bg-cosmos-800 disabled:opacity-40'
                    }
                  >
                    {label}
                  </button>
                );
              })()}
              <button
                type="button"
                onClick={() => dispatchAction({ kind: 'PassTurn' })}
                className="rounded border border-cosmos-700 px-3 py-1.5 text-xs hover:bg-cosmos-800"
                title="Pass initiative to your opponent"
              >
                Pass
              </button>
              <button
                type="button"
                onClick={() => dispatchAction({ kind: 'AdvanceRound' })}
                disabled={round >= 7}
                className="rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-40"
                title="Advance to the next round (opens the next realm lane)"
              >
                End Round
              </button>
            </div>
          </div>

          {/* Your hand */}
          <Hand
            owner={viewer}
            cardIds={hand}
            cards={cards.cards}
            imageUrl={cards.imageUrl}
            onCardContextMenu={onCardContextMenu}
          />

          {/* Zone-peek modal for the classic chrome stacks. Public
              zones (abyss / banishment / tokens) get a `source` so
              peek-cards are draggable; the deck is private and stays
              read-only. */}
          {classicPeek && (
            <ZonePeek
              title={
                classicPeek.kind === 'deck'
                  ? 'Your Deck'
                  : classicPeek.kind === 'abyss'
                    ? 'Your Abyss'
                    : classicPeek.kind === 'tokens'
                      ? 'Your Tokens'
                      : 'Your Banishment'
              }
              cardIds={classicPeek.cardIds}
              visibility={classicPeek.kind === 'deck' ? 'private' : 'public'}
              cards={cards.cards}
              imageUrl={cards.imageUrl}
              onClose={() => setClassicPeek(null)}
              {...(classicPeek.kind === 'abyss' ||
              classicPeek.kind === 'banished' ||
              classicPeek.kind === 'tokens'
                ? { source: { zone: classicPeek.kind, owner: viewer } }
                : {})}
            />
          )}

          {/* Move-rejected toast */}
          {moveError && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-rose-700 text-cosmos-50 px-4 py-2 rounded shadow-xl">
              {moveError}
            </div>
          )}

          {/* Emote palette — bottom-right floating column */}
          <EmotePalette onEmote={(emoji) => dispatchAction({ kind: 'EmoteReaction', emoji })} />

          {/* First-player roll banner — shows the d20 result and who
              plays first. Auto-dismissable; renders for the first
              ~6 seconds of the match (or until clicked). Sandbox
              doesn't enforce turn-order, so this is purely a fairness
              cue for testers — both players see the same numbers. */}
          {state.firstPlayer && !firstPlayerAck && !state.outcome && (
            // biome-ignore lint/a11y/useKeyWithClickEvents: dismissible banner — click anywhere to ack.
            // biome-ignore lint/a11y/noStaticElementInteractions: same.
            <div
              className="absolute top-1/4 left-1/2 z-40 -translate-x-1/2 cursor-pointer rounded-tile bg-cosmos-900/95 px-6 py-4 text-center shadow-2xl ring-2 ring-amber-500"
              onClick={() => setFirstPlayerAck(true)}
              title="Click to dismiss"
            >
              <h2 className="font-display text-2xl text-amber-200">
                {state.firstPlayer === viewer ? 'You go first' : 'Opponent goes first'}
              </h2>
              <p className="mt-1 font-mono text-cosmos-400 text-xs">
                Roll —{' '}
                <span
                  className={state.firstPlayer === viewer ? 'text-amber-200' : 'text-cosmos-300'}
                >
                  you {state.firstPlayerRoll?.[viewer] ?? '?'}
                </span>{' '}
                ·{' '}
                <span
                  className={state.firstPlayer !== viewer ? 'text-amber-200' : 'text-cosmos-300'}
                >
                  opp {state.firstPlayerRoll?.[opponent] ?? '?'}
                </span>
              </p>
              <p className="mt-2 text-cosmos-500 text-[10px]">click to dismiss</p>
            </div>
          )}

          {/* Match outcome banner */}
          {state.outcome && (
            <div className="absolute inset-0 flex items-center justify-center bg-cosmos-950/80 backdrop-blur z-50">
              <div className="bg-cosmos-900 ring-1 ring-cosmos-700 rounded-tile p-8 text-center max-w-md">
                <h2 className="font-display text-3xl mb-2">Match ended</h2>
                <p className="text-cosmos-300 mb-4">
                  {state.outcome.kind === 'concede'
                    ? `${shortName(state.outcome.declaredBy)} conceded.`
                    : `${shortName(state.outcome.declaredBy)} declared victory.`}
                </p>
                {state.outcome.note && (
                  <p className="italic text-cosmos-400 mb-4">"{state.outcome.note}"</p>
                )}
              </div>
            </div>
          )}
        </main>

        <div className="flex w-80 flex-col border-cosmos-700 border-l">
          <div className="p-2">
            <VoiceChat {...voice} />
          </div>
          {/* Rules helper — collapsible reference panel pulled from
              skills/wonders-rules-v1/SKILL.md. Default-collapsed so
              it doesn't take vertical space until needed. */}
          <RulesHelperPanel />
          {/* Game log mirrors the Fullscreen2D right rail. Renders every
              TableActionApplied event so the player can audit moves,
              cost-pays, and ability triggers in real time. Sized to
              ~40% of the side rail so the chat/dice tabs below remain
              accessible. */}
          <div className="flex h-1/2 min-h-[180px] flex-col border-cosmos-800 border-b">
            <GameLogPanel events={allEvents} viewer={viewer} cards={cards.cards} />
          </div>
          <SidePanel
            state={state}
            viewer={viewer}
            onSendChat={(text) => dispatchAction({ kind: 'ChatMessage', text })}
            onRollDice={(sides) => {
              const result = Math.floor(Math.random() * sides) + 1;
              dispatchAction({ kind: 'RollDice', sides, result });
            }}
            onConcede={() => dispatchAction({ kind: 'ConcedeMatch' })}
            onDeclareVictory={() => dispatchAction({ kind: 'DeclareVictory' })}
          />
        </div>
      </div>

      {/* Floating emote overlay — full-screen above board */}
      <EmoteOverlay emotes={emoteEvents} />

      {/* AI Coach overlay — fixed top-right; collapses to a chip. */}
      {coachOverlay}
      {mulliganOverlay}
      {globalOverlays}

      {/* Context menu — fixed-positioned at click coords */}
      {contextMenu && (
        <CounterMenu
          x={contextMenu.x}
          y={contextMenu.y}
          cardId={contextMenu.cardId}
          isFaceDown={contextMenu.placement?.faceDown ?? false}
          isTapped={contextMenu.placement?.tapped ?? false}
          onTap={(id) => dispatchAction({ kind: 'TapCard', cardId: id })}
          onUntap={(id) => dispatchAction({ kind: 'UntapCard', cardId: id })}
          onFlip={(id, faceDown) => dispatchAction({ kind: 'FlipCard', cardId: id, faceDown })}
          onAddCounter={(id, counter, n) =>
            dispatchAction({ kind: 'AddCounter', cardId: id, counter, n })
          }
          onRemoveCounter={(id, counter, n) =>
            dispatchAction({ kind: 'RemoveCounter', cardId: id, counter, n })
          }
          onMoveTo={(id, dest) => {
            const source = inferSource(state, viewer, id);
            if (!source) return;
            dispatchAction({
              kind: 'MoveCard',
              cardId: id,
              from: source,
              to: { kind: dest, owner: viewer },
            });
          }}
          onClose={() => setContextMenu(null)}
        />
      )}
    </DndContext>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function findPlacement(state: PublicTableState | null, cardId: string): Placement | null {
  if (!state) return null;
  for (const realm of state.realms) {
    const placement = realm.placements.find((p) => p.cardId === cardId);
    if (placement) return placement;
  }
  return null;
}

function inferSource(state: PublicTableState, viewer: PlayerId, cardId: string): ZoneRef | null {
  // First check the board.
  for (const realm of state.realms) {
    const p = realm.placements.find((pl) => pl.cardId === cardId);
    if (p) return { kind: 'realm', realm: realm.name, owner: p.owner };
  }
  // Then check viewer's private zones.
  const zones = state.zones[viewer];
  if (zones && 'hand' in zones) {
    if (zones.hand.includes(cardId)) return { kind: 'hand', owner: viewer };
    if (zones.deck.includes(cardId)) return { kind: 'deck', owner: viewer };
    if (zones.abyss.includes(cardId)) return { kind: 'abyss', owner: viewer };
    if (zones.banished.includes(cardId)) return { kind: 'banished', owner: viewer };
  }
  return null;
}

function actionFeatureName(action: TableAction): string {
  return action.kind;
}

function shortName(player: PlayerId): string {
  return player.length > 12 ? `${player.slice(0, 8)}…` : player;
}

/**
 * PileColumn — chrome-bar two-row tile for one pile zone.
 *
 *   ┌─────────────────────┐
 *   │  ZoneStack (top)    │  ← drop = put on top
 *   │  count + label      │
 *   ├─────────────────────┤
 *   │  Bury outline       │  ← drop = bury at bottom of pile
 *   └─────────────────────┘
 *
 * Used for Deck / Abyss / Exile so testers can drop a card precisely
 * to either the top or the bottom of any pile without a context menu.
 */
function PileColumn({
  label,
  tone,
  count,
  onClick,
  topId,
  topData,
  buryId,
  buryData,
  topTooltip,
  topCardDragId,
  topCardDragData,
}: {
  label: string;
  tone: 'amber' | 'rose' | 'violet';
  count: number;
  onClick?: () => void;
  topId: string;
  topData: Record<string, unknown>;
  buryId: string;
  buryData: Record<string, unknown>;
  topTooltip: string;
  /** When the pile has cards, the stack is also a drag source for the
   *  pile's top card — alternative draw mechanism: drag the top of the
   *  deck / abyss / banishment directly to hand or any realm. */
  topCardDragId?: string;
  topCardDragData?: Record<string, unknown>;
}) {
  // Two-tile horizontal row: large pile on the left, narrow Bury slot
  // on the right. Keeps the whole column to a single 1-row strip so
  // the chrome bar reads as one band of widgets rather than the prior
  // 2-row stack.
  return (
    <div className="flex items-stretch gap-1">
      <ZoneStack
        label={label}
        count={count}
        tone={tone}
        tooltip={topTooltip}
        droppableId={topId}
        droppableData={topData}
        {...(onClick ? { onClick } : {})}
        {...(topCardDragId && topCardDragData
          ? { draggableId: topCardDragId, dragData: topCardDragData }
          : {})}
      />
      <BuryOutline tone={tone} droppableId={buryId} droppableData={buryData} />
    </div>
  );
}

/**
 * Card-shaped outline drop slot beneath a PileColumn. No fill; takes
 * the lane's tone for the dashed border so the slot reads as a
 * variant of the pile above it. Highlights on drag-over.
 */
function BuryOutline({
  tone,
  droppableId,
  droppableData,
}: {
  tone: 'amber' | 'rose' | 'violet';
  droppableId: string;
  droppableData: Record<string, unknown>;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: droppableId, data: droppableData });
  const border =
    tone === 'amber'
      ? 'border-amber-700/60'
      : tone === 'rose'
        ? 'border-rose-700/60'
        : 'border-violet-700/60';
  const text =
    tone === 'amber' ? 'text-amber-300' : tone === 'rose' ? 'text-rose-300' : 'text-violet-300';
  return (
    <div
      ref={setNodeRef}
      title={`Bury at the bottom of the ${tone === 'amber' ? 'deck' : tone === 'rose' ? 'abyss' : 'banishment'} pile`}
      className={`flex h-32 w-8 flex-col items-center justify-center rounded-sm border-2 border-dashed font-mono text-[9px] uppercase tracking-tight [writing-mode:vertical-rl] ${border} ${text} ${isOver ? 'bg-cosmos-800 ring-2 ring-cyan-300 ring-offset-1 ring-offset-cosmos-900' : ''}`}
    >
      Bury
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center h-screen w-screen bg-cosmos-950">
      {children}
    </div>
  );
}

function OpponentStrip({ opponent, state }: { opponent: PlayerId; state: PublicTableState }) {
  // Mirror of the bottom chrome bar — same layout, same widget set,
  // but read-only: opponent piles aren't drop targets and aren't drag
  // sources from the viewer's POV. Cross-player edits flow through
  // the owner-approval action (RequestZoneEdit / ResolveZoneEdit).
  const zones = state.zones[opponent];
  const handCount =
    zones && 'handCount' in zones
      ? zones.handCount
      : zones && 'hand' in zones
        ? zones.hand.length
        : 0;
  const oppDeck =
    zones && 'deck' in zones && Array.isArray(zones.deck)
      ? zones.deck.length
      : zones && 'deckCount' in zones
        ? ((zones as { deckCount?: number }).deckCount ?? 0)
        : 0;
  const oppAbyss =
    zones && 'abyss' in zones && Array.isArray(zones.abyss)
      ? zones.abyss.length
      : zones && 'abyssCount' in zones
        ? ((zones as { abyssCount?: number }).abyssCount ?? 0)
        : 0;
  const oppBanished =
    zones && 'banished' in zones && Array.isArray(zones.banished)
      ? zones.banished.length
      : zones && 'banishedCount' in zones
        ? ((zones as { banishedCount?: number }).banishedCount ?? 0)
        : 0;
  const oppTokens =
    zones && 'tokens' in zones && Array.isArray(zones.tokens) ? zones.tokens.length : 0;
  const oppRes = state.resources?.[opponent] ?? { energy: 0, actions: 0, hoard: 0 };
  const round = Math.max(1, state.round ?? 0);
  return (
    <div className="flex items-center gap-3 border-cosmos-700 border-b bg-cosmos-900/60 px-3 py-2">
      <span className="font-bold text-cosmos-300 text-sm">{shortName(opponent)}</span>
      <ReadOnlyPile label="DECK" count={oppDeck} tone="amber" />
      <ReadOnlyPile label="ABYSS" count={oppAbyss} tone="rose" />
      <ReadOnlyPile label="BAN." count={oppBanished} tone="violet" />
      <ReadOnlyPile label="TOK." count={oppTokens} tone="amber" />
      <ReadOnlyPill
        icon={<Zap className="h-3.5 w-3.5 text-amber-300" />}
        value={oppRes.energy}
        label="Energy"
      />
      <ReadOnlyPill
        icon={<Target className="h-3.5 w-3.5 text-rose-300" />}
        value={oppRes.actions}
        label="Actions"
      />
      <ReadOnlyPill
        icon={<Coins className="h-3.5 w-3.5 text-violet-300" />}
        value={oppRes.hoard}
        label="Hoard"
      />
      <span className="font-mono text-[11px] text-cosmos-400">
        R{round}/7 {state.initiative === opponent ? '· their turn' : ''}
      </span>
      <div className="ml-auto flex flex-1 justify-end gap-0.5 overflow-hidden">
        {Array.from({ length: Math.min(handCount, 14) }).map((_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: rendering N identical card-back placeholders; the index is the only stable identity available.
            key={`opp-back-${i}`}
            className="h-12 w-8 flex-shrink-0 rounded-sm bg-gradient-to-br from-cyan-700 to-cyan-950 ring-1 ring-cyan-600/40"
          />
        ))}
        {handCount > 14 && (
          <span className="ml-1 self-center font-mono text-[11px] text-cosmos-400">
            +{handCount - 14}
          </span>
        )}
      </div>
    </div>
  );
}

function ReadOnlyPile({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'amber' | 'rose' | 'violet';
}) {
  const ring =
    tone === 'amber'
      ? 'ring-amber-700/60'
      : tone === 'rose'
        ? 'ring-rose-700/60'
        : 'ring-violet-700/60';
  const bg =
    tone === 'amber'
      ? 'from-amber-700 to-amber-950'
      : tone === 'rose'
        ? 'from-rose-800 to-rose-950'
        : 'from-violet-800 to-violet-950';
  const text =
    tone === 'amber' ? 'text-amber-200' : tone === 'rose' ? 'text-rose-200' : 'text-violet-200';
  return (
    <div
      className="relative flex h-12 w-9 flex-col items-center justify-end"
      title={`${label} ${count}`}
    >
      {count > 0 ? (
        <span
          className={`absolute inset-x-0 top-0 h-9 rounded-sm bg-gradient-to-br ${bg} ring-1 ${ring}`}
        />
      ) : (
        <span className="absolute inset-x-0 top-0 h-9 rounded-sm border border-cosmos-700 border-dashed" />
      )}
      <span
        className={`relative z-10 mt-auto rounded bg-cosmos-950/90 px-1 font-bold font-mono text-[9px] ${text}`}
      >
        {label} {count}
      </span>
    </div>
  );
}

function ReadOnlyPill({
  icon,
  value,
  label,
}: {
  icon: string | React.ReactNode;
  value: number;
  label: string;
}) {
  return (
    <div
      className="flex items-center gap-1 rounded border border-cosmos-700 bg-cosmos-800/80 px-2 py-0.5 text-xs"
      title={label}
    >
      <span aria-hidden="true" className="flex items-center">
        {icon}
      </span>
      <motion.span
        key={value}
        initial={{ scale: 1.5, color: '#fda4af' }}
        animate={{ scale: 1, color: 'inherit' }}
        transition={{ duration: 0.32, ease: 'easeOut' }}
        className="font-mono"
      >
        {value}
      </motion.span>
    </div>
  );
}
